import Footer from '@components/common/footer'
import NavBar from '@components/common/navbar'
import TextImageCommonFold from '@components/common/text-image-common-fold'
import { Investors } from '@components/book-trial-class-jp/investors'
import CodingalOffering from '@components/kids-teens-coding-camps/codingal-offering'
import Courses from '@components/kids-teens-coding-camps/courses'
import FirstFold from '@components/kids-teens-coding-camps/first-fold'
import Articles from '@components/home/articles'
import Faq from '@components/home/faq'
import MoneyBackGuarantee from '@components/home/guarantee'
import Teachers from '@components/home/teachers'
import Partners from '@components/locale-pages/home/us/partners'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import { FaqContentType } from '@lib/data/faq'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { formatSeoConfig } from '@lib/seo'
import date from '@lib/utils/date.utils'
import { msgT } from '@lib/utils/i18n'
import { defineMessage, Trans } from '@lingui/macro'
import { FAQPageJsonLd, NextSeo } from 'next-seo'
import Link from 'next/link'

const XoxodayCodingCampSeo = formatSeoConfig({
  title: `Xoxoday Exclusive Coding Camp for Kids & Teens ${date().year()} | Codingal`,
  description: `Unlock Xoxoday-exclusive pricing on Codingal's live online coding camp for kids and teens ages 7-17. Explore Scratch, Python, Roblox, web development, app development, and data science through project-based classes.`,
  url: 'https://www.codingal.com/kids-teens-coding-camps/xoxoday/',
  metaImage: 'https://www.codingal.com/images/coding-camp/summer-og.jpg',
})

const couponCode = 'xoxoday50'
const year = date().year()

export const FAQ_CONTENTS: FaqContentType[] = [
  {
    questionName: defineMessage({
      message: `What is Codingal's Xoxoday Exclusive Coding Camp ${year}?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal&apos;s Xoxoday Exclusive Coding Camp {year} is a live online
        coding experience for eligible Xoxoday users and their families. Kids
        and teens can build games, apps, websites, and AI projects with expert
        guidance in a short, structured camp format.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal's Xoxoday Exclusive Coding Camp ${year} is a live online coding experience for eligible Xoxoday users and their families. Kids and teens can build games, apps, websites, and AI projects with expert guidance in a short, structured camp format.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Who can join this coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        The camp is designed for learners ages 7-17. We offer beginner-friendly
        and advanced tracks so elementary, middle, and high school students can
        choose a camp that matches their age and experience level.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The camp is designed for learners ages 7-17. We offer beginner-friendly and advanced tracks so elementary, middle, and high school students can choose a camp that matches their age and experience level.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Does my child need previous coding experience?`,
    }),
    acceptedAnswerText: (
      <>
        No. Beginners can start with foundational coding tracks like Scratch or
        Python Foundations, while experienced learners can choose advanced paths
        in Python, Roblox, app development, web development, or data science.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `No. Beginners can start with foundational coding tracks like Scratch or Python Foundations, while experienced learners can choose advanced paths in Python, Roblox, app development, web development, or data science.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What topics can my child explore in this coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Depending on the chosen track, learners can explore Scratch, Python,
        Roblox Studio, Lua, HTML, CSS, JavaScript, app development, and data
        science through project-based lessons.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Depending on the chosen track, learners can explore Scratch, Python, Roblox Studio, Lua, HTML, CSS, JavaScript, app development, and data science through project-based lessons.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How are the coding camp classes conducted?`,
    }),
    acceptedAnswerText: (
      <>
        All sessions are taught live online by expert instructors on
        Codingal&apos;s interactive learning platform. Learners receive
        real-time guidance, feedback on projects, and structured support
        throughout the camp.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `All sessions are taught live online by expert instructors on Codingal's interactive learning platform. Learners receive real-time guidance, feedback on projects, and structured support throughout the camp.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What projects will students build during the camp?`,
    }),
    acceptedAnswerText: (
      <>
        Students work on age-appropriate, hands-on projects such as games in
        Scratch, Python applications, Roblox experiences, interactive websites,
        and beginner data science explorations. Every track is designed to end
        with projects learners can proudly showcase.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Students work on age-appropriate, hands-on projects such as games in Scratch, Python applications, Roblox experiences, interactive websites, and beginner data science explorations. Every track is designed to end with projects learners can proudly showcase.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How do I apply the Xoxoday coupon code?`,
    }),
    acceptedAnswerText: (
      <>
        Select your child&apos;s preferred camp track from this page and proceed
        to checkout. The offer code <strong>xoxoday50</strong> is displayed on
        the page and can be copied directly while completing registration.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Select your child's preferred camp track from this page and proceed to checkout. The offer code xoxoday50 is displayed on the page and can be copied directly while completing registration.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What is included in the Xoxoday exclusive offer?`,
    }),
    acceptedAnswerText: (
      <>
        Eligible Xoxoday users get access to Codingal&apos;s live online camp
        tracks at exclusive pricing using the coupon code on this page. The
        offer applies to the same instructor-led, project-based coding camp
        experience available to Codingal learners.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Eligible Xoxoday users get access to Codingal's live online camp tracks at exclusive pricing using the coupon code on this page. The offer applies to the same instructor-led, project-based coding camp experience available to Codingal learners.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Will my child receive a certificate after the coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Yes. Learners receive a certificate after completing their coding camp
        track, helping them celebrate progress and showcase the projects and
        skills they built during the program.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. Learners receive a certificate after completing their coding camp track, helping them celebrate progress and showcase the projects and skills they built during the program.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What setup does my child need to attend the camp?`,
    }),
    acceptedAnswerText: (
      <>
        Learners need a laptop or computer with a stable internet connection.
        Codingal provides the teaching environment, project guidance, and
        curriculum support needed for the selected course.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Learners need a laptop or computer with a stable internet connection. Codingal provides the teaching environment, project guidance, and curriculum support needed for the selected course.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What support does Codingal provide beyond live sessions?`,
    }),
    acceptedAnswerText: (
      <>
        Families benefit from expert instructors, structured project-based
        learning, personalised guidance, and continued momentum into more
        advanced Codingal programs after the camp ends.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Families benefit from expert instructors, structured project-based learning, personalised guidance, and continued momentum into more advanced Codingal programs after the camp ends.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What is Codingal's refund policy for this camp?`,
    }),
    acceptedAnswerText: (
      <>
        If the coding camp does not meet your expectations, you can cancel and
        receive a full refund. Email{' '}
        <a
          href="mailto:support@codingal.com"
          target="_blank"
          rel="noreferrer"
          className="text-blue-200 underline"
        >
          support@codingal.com
        </a>{' '}
        to request a refund for any remaining classes.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `If the coding camp does not meet your expectations, you can cancel and receive a full refund. Email support@codingal.com to request a refund for any remaining classes.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Why should I trust Codingal for my child?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal was founded by experienced edtech entrepreneurs, IIT alumni,
        and former leaders from Google and Amazon, with backing from investors
        like Y Combinator and Rebright Partners. Learn more about us{' '}
        <Link href="/about-us" prefetch={false}>
          <a className="text-blue-200 underline">here</a>
        </Link>
        .
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal was founded by experienced edtech entrepreneurs, IIT alumni, and former leaders from Google and Amazon, with backing from investors like Y Combinator and Rebright Partners. Learn more about us here.`,
    }),
  },
]

const XoxodayCodingCampPage = () => {
  return (
    <>
      <NextSeo {...XoxodayCodingCampSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ_CONTENTS.map(
          ({ acceptedAnswerTextString, questionName }) => ({
            acceptedAnswerText:
              typeof acceptedAnswerTextString === 'string'
                ? acceptedAnswerTextString
                : msgT(acceptedAnswerTextString),
            questionName:
              typeof questionName === 'string'
                ? questionName
                : msgT(questionName),
          })
        )}
      />
      <NavBar isSticky />
      <FirstFold
        title={
          <>
            <Trans>Coding Camp for Kids & Teens</Trans> {year}
          </>
        }
        description={
          <>
            <p className="text-center lg:text-left text-sm lg:text-base">
              <Trans>
                Welcome to the Xoxoday exclusive Codingal offer for kids and
                teens. This live online coding camp helps learners build
                real-world projects in Scratch, Python, Roblox, Web Development,
                App Development, and Data Science with expert instructor
                support.
              </Trans>
            </p>
            <p className="text-center lg:text-left pb-2 text-sm lg:text-base">
              <Trans>
                Choose the right track for your child&apos;s age and experience,
                unlock special pricing with the Xoxoday offer code, and give
                them a structured, hands-on camp experience that builds
                creativity, confidence, and future-ready skills.
              </Trans>
            </p>
          </>
        }
        couponCode={couponCode}
        coursePrice={149}
        discountedPrice={75}
        offerPercentage={50}
        imagePath="/images/coding-summer-camp/course-image"
        imageAlt="Coding camp for kids and teens with Xoxoday exclusive offer"
        backgroundColour="#18244f"
        urgencyMessage=""
        partnerBrand={{
          logoSrc: '/images/common/partners/xoxoday.png',
          logoAlt: 'Xoxoday logo',
          logoWidth: 170,
          logoHeight: 43,
        }}
      />
      <Partners />
      <Courses
        couponCode={couponCode}
        title={<Trans>Choose the right coding camp track for your child</Trans>}
      />
      <VideoTestimonial
        title="What parents and students say about Codingal's coding camps"
        video_testimonial={VIDEO_TESTIMONIAL}
      />
      <TrustpilotReview isCampPage />
      <Teachers
        isShowBecomeTeacher={false}
        title="Learn coding from the best IT instructors"
        ctaLink="#courses"
        ctaText="Claim your spot now"
      />
      <CodingalOffering
        title={
          <Trans>Why Xoxoday families choose Codingal&apos;s coding camp</Trans>
        }
      />
      <MoneyBackGuarantee
        isCodingCampPage
        title="Why Xoxoday families choose Codingal's coding camp"
      />
      <TextImageCommonFold
        title={
          <>
            A live coding camp built around{' '}
            <span className="yellow-underline">
              guided projects and real progress
            </span>
          </>
        }
        description={
          <>
            <p>
              This coding camp gives kids and teens a focused, engaging path
              into technology through instructor-led online sessions.
            </p>
            <p>
              Learners build real projects in age-appropriate tracks spanning
              Scratch, Python, Web Development, App Development, Roblox, and
              Data Science.
            </p>
            <p>
              Every class is hands-on, interactive, and designed to improve
              problem-solving, creativity, and technical confidence from the
              very first session.
            </p>
            <p>
              With the Xoxoday exclusive offer, families can enroll in the same
              premium Codingal camp experience at a special price.
            </p>
          </>
        }
        imagePath="/images/coding-camp/summer-camp-activities"
        imageWidth={370}
        imageHeight={373}
      />
      <TextImageCommonFold
        title={
          <>
            STEM.org Accredited{' '}
            <span className="yellow-underline">learning experience</span> and
            certificates
          </>
        }
        description={
          <>
            <p>
              Codingal offers a STEM.org-accredited curriculum that helps kids
              and teens build a strong coding foundation while learning through
              guided, project-based instruction.
            </p>
            <p>
              Students learn practical concepts, complete projects they can
              showcase, and receive recognition for the skills they develop
              during the camp.
            </p>
          </>
        }
        imagePath="/images/coding-camp/summer-camp-certificate"
        imageWidth={370}
        imageHeight={370}
      />
      <TextImageCommonFold
        title={
          <>
            Build future-ready{' '}
            <span className="yellow-underline">
              coding skills and certifications
            </span>
          </>
        }
        description={
          <>
            <p>
              From first-time coders to learners ready for advanced projects,
              Codingal&apos;s camp tracks are designed to turn curiosity into
              real technical ability.
            </p>
            <p>
              Through hands-on classes, students create games, websites, apps,
              and creative coding projects while strengthening logical thinking
              and digital problem-solving.
            </p>
            <p>
              Each completed camp track ends with a certificate that helps
              learners celebrate progress and showcase what they have built.
            </p>
          </>
        }
        imagePath="/images/coding-camp/summer-camp-certificate-2"
        imageWidth={370}
        imageHeight={370}
      />
      <TextImageCommonFold
        title={
          <>
            The best coding camp for{' '}
            <span className="yellow-underline">
              kids to learn fundamentals and grow fast
            </span>
          </>
        }
        description={
          <>
            <p>
              Our coding camp combines structured teaching with creative project
              work, making it easy for kids and teens to stay engaged while
              building practical skills.
            </p>
            <p>
              Families can choose from multiple tracks like Scratch Explorer,
              Scratch Champion, Python Foundations, Python Champion, Website
              Wizard, Website Champion, App Inventor, App Champion, Roblox
              Rookie, Roblox Champion, Data Science Intro, and Data Science
              Champion.
            </p>
            <p>
              With six instructor-led sessions per program, learners can explore
              the topic that fits them best and make measurable progress in a
              short time.
            </p>
          </>
        }
        imagePath="/images/coding-camp/summer-camp-student-learning"
        imageWidth={370}
        imageHeight={373}
      />
      <TextImageCommonFold
        title={
          <>
            Our coding camp is designed for{' '}
            <span className="yellow-underline">
              elementary, middle, and high school learners
            </span>
          </>
        }
        description={
          <>
            <p>
              Codingal&apos;s coding camp supports students aged 7-17 with
              tracks tailored to different ages, goals, and skill levels.
            </p>
            <p>
              Whether your child is just getting started or ready for advanced
              programming concepts, they can learn at the right pace with expert
              guidance and project-based instruction.
            </p>
          </>
        }
        imagePath="/images/coding-camp/summer-camp-school-kids"
        imageWidth={370}
        imageHeight={373}
      />
      <Faq
        title="Questions from Xoxoday families? Find answers here"
        faqContents={FAQ_CONTENTS}
      />
      <Articles />
      <Investors isCodingCampPage />
      <Footer />
    </>
  )
}

export default XoxodayCodingCampPage
